import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ApiError, api } from "@shared/api";
import { ATTACHMENT_MAX } from "@shared/config";
import { refusal, useStore } from "@features/chat";
import { useMe } from "@entities/session";
import { useCatalogue } from "@entities/workspace";
import { Menu } from "@shared/ui/Menu";
import { StampDialog } from "./StampDialog";
import { PollDialog } from "./PollDialog";
import { ExpressionPicker, type ExpressionTab } from "./ExpressionPicker";
import { COMMANDS, quickEdit, runCommand, type Command } from "../lib/commands";
import {
  autocompleteOf,
  currentOption,
  optionKind,
  OPTION,
  parseCommand,
  typedCommand,
  type Typed,
} from "../lib/app-commands";
import { listen as listenCalls, useInteractions } from "../model/interactions";
import { hydrate, pillAt, rebase, serialize, type Pill, type PillNames } from "../lib/pills";
import { FileImage } from "@shared/ui/FileImage";
import {
  useEmojiSet,
  searchCustom,
  searchEmoji,
  skinTone,
  withTone,
  type CustomEmoji,
} from "@shared/lib/emoji";
import type { Channel, ChannelCommand, CommandChoice, CommandOption, Named, Role, ServerSticker, Uuid } from "@shared/api";
import {
  IconClose,
  IconEmoji,
  IconImagePlay,
  IconSticker,
  IconMic,
  IconPlus,
  IconSend,
  IconTrash,
  IconEye,
  IconEyeOff,
  IconEnter,
  IconQuoted,
  IconTimer,
  IconTab,
  IconTextChannel,
  IconVoiceChannel,
  IconStageChannel,
} from "@shared/ui/icons";
import { Popover, PopoverAnchor, PopoverContent } from "@shared/ui/popover";
import { Spinner } from "@shared/ui/spinner";
import { Dialog, Field } from "@shared/ui/bits";
import { Button } from "@shared/ui/button";
import { Textarea } from "@shared/ui/textarea";
import { recordVoice, type VoiceRecording } from "@shared/media";
import { holdRestartBlocker, readDraft, writeDraft } from "@shared/lib/desktop";
import { useRestartBlocker, useRestartPreparation } from "@features/updater";
import { GooBar } from "@shared/motion";
import { isCall } from "@entities/channel";

/**
 * Как часто сообщать, что набирают.
 *
 * Событие продлевает надпись у остальных на семь секунд, поэтому чаще незачем,
 * а на каждую букву — это обстрел сервера запросами. Четыре меньше семи с
 * запасом: надпись не успеет погаснуть между двумя нашими запросами.
 */
const TYPING_EVERY = 4000;

/** Высота капель поля ввода в одну строку. В макете v6 — 52, у нас 56, вровень со своей строкой слева. */
const ROW = 56;

/**
 * Кто печатает — строкой.
 *
 * Имён показываем два, дальше числом: три имени подряд длиннее самой строки
 * ввода, а читают эту подпись боковым зрением.
 */
/** Текст поля для зеркала: куски плашек — плашками, в конце — опора для последней пустой строки. */
function mirrored(text: string, pills: readonly Pill[]) {
  const out: ReactNode[] = [];
  let at = 0;
  for (const pill of [...pills].sort((a, b) => a.start - b.start)) {
    out.push(text.slice(at, pill.start));
    out.push(
      <span key={pill.start} className="composer-pill">
        {text.slice(pill.start, pill.end)}
      </span>,
    );
    at = pill.end;
  }
  out.push(`${text.slice(at)}\u200b`);
  return out;
}

function typingLine(ids: Uuid[], people: Map<Uuid, Named>): string {
  const names = ids.map((id) => {
    const person = people.get(id);
    return person?.display_name || person?.username || "кто-то";
  });

  if (names.length === 1) return `${names[0]} печатает…`;
  if (names.length === 2) return `${names[0]} и ${names[1]} печатают…`;

  return `${names[0]}, ${names[1]} и ещё ${names.length - 2} печатают…`;
}

interface QueuedFile {
  id: Uuid;
  name: string;
  /** Спойлер — закрыт у всех, пока не нажмут. */
  spoiler?: boolean;
  /** Описание — для читалки экрана и подписи по наведению. */
  description?: string;
}

/**
 * Файл, который сейчас едет на сервер.
 *
 * Держится отдельно от `QueuedFile`, а не общим списком с необязательным
 * `id`: у отправки список уже готовых, и «может быть, без номера» пришлось бы
 * отсеивать в каждом месте, где он берётся.
 *
 * Свой ключ, а не имя: бросают и по два одинаковых файла сразу.
 */
interface LoadingFile {
  key: number;
  name: string;
  sent: number;
  total: number;
}


/**
 * Что подставляет подсказка над полем.
 *
 * Имена и знаки идут одним списком не для экономии: подсказка над полем одна,
 * стрелки и Enter принадлежат ей целиком, и развести это на две независимые
 * подсказки значит рано или поздно открыть обе разом.
 */
type Suggestion =
  | { kind: "command"; command: Command }
  /** Команда бота сервера — над встроенными, как в Discord. */
  | { kind: "app"; command: ChannelCommand }
  /** Вариант автодополнения от бота — для параметра, который сейчас набирают. */
  | { kind: "choice"; choice: CommandChoice; start: number }
  | { kind: "person"; person: Named }
  | { kind: "everyone"; word: "everyone" | "here" }
  | { kind: "role"; role: Role }
  | { kind: "channel"; channel: Channel }
  | {
      kind: "emoji";
      /** Что попадёт в текст: сам знак либо `:имя:`. */
      insert: string;
      /** Как знак показать в строке подсказки. */
      mark: string;
      custom: CustomEmoji | null;
      /** Подпись под знаком — шорткод или имя. */
      token: string;
    };

/** Ключ строки подсказки: у каждого вида свой опознавательный признак. */
function suggestionKey(item: Suggestion, index: number): string {
  switch (item.kind) {
    case "app":
      return `app:${item.command.id}`;
    case "command":
      return `cmd:${item.command.name}`;
    case "person":
      return item.person.id;
    case "everyone":
      return item.word;
    case "role":
      return item.role.id;
    case "channel":
      return item.channel.id;
    case "choice":
      return `choice:${String(item.choice.value)}:${index}`;
    default:
      return item.token + index;
  }
}

/** Заголовок группы в подсказке «/»: команды бота — под его именем, встроенные — своим. */
function commandHead(list: Suggestion[], index: number): string | null {
  const group = (item?: Suggestion) =>
    item?.kind === "app" ? item.command.application_id : item?.kind === "command" ? "builtin" : null;
  const item = list[index];
  const here = group(item);
  if (!here || here === group(list[index - 1])) return null;
  return item.kind === "app" ? item.command.application_name : "Встроенные команды";
}

/** Незакрытое «@…», «#…» или «:…» слева от курсора; «/…» в начале поля — команда. */
type Hint = { kind: "mention" | "channel" | "emoji" | "command"; at: number; query: string };

/**
 * Сколько букв нужно после двоеточия, чтобы подсказка открылась.
 *
 * Двух: с одной смайлики полезли бы на каждое «10:30», а расписание в
 * переписке встречается чаще, чем знак по имени.
 */
const EMOJI_HINT_MIN = 2;

/**
 * Команды ботов по каналам. Боты правят их редко, а «/» набирают часто: список
 * держится минуту и не спрашивается на каждую косую черту.
 */
const commandCache = new Map<Uuid, { at: number; items: ChannelCommand[] }>();
/** Сколько набор должен замереть, прежде чем спросить у бота варианты. */
const AUTOCOMPLETE_PAUSE = 250;

const COMMANDS_FRESH = 60_000;

/** Сроки медленного режима по каналам и веткам: до какого времени писать нельзя. */
const cooldowns = new Map<string, number>();

/** «0:27» — сколько осталось, с округлением вверх: ноль секунд не показываем. */
function left(ms: number): string {
  const total = Math.max(1, Math.ceil(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = String(total % 60).padStart(2, "0");
  return hours ? `${hours}:${String(minutes).padStart(2, "0")}:${seconds}` : `${minutes}:${seconds}`;
}

export function Composer({
  channelId,
  placeholder,
  disabled,
  disabledReason,
  people,
  onUnblock,
  canUpload,
  custom,
  channels,
  roles,
  onEditLast,
  onModerate,
  onDirectMessage,
  focus = 0,
  serverId = null,
  slowmode = 0,
  canEveryone = false,
  stickers = [],
  canPoll = false,
  canVoiceMessage = true,
  thread,
}: {
  channelId: Uuid;
  placeholder: string;
  /** Кого можно назвать: состав сервера, друзья, собеседники. */
  people: Map<Uuid, Named>;
  /** Какие каналы можно назвать через «#». В переписке список пуст. */
  channels: Channel[];
  /** Какие роли можно назвать через «@». В переписке список пуст. */
  roles: Role[];
  disabled?: boolean;
  disabledReason?: string;
  onUnblock?: () => void;
  canUpload: boolean;
  /** Знаки этого сервера. В личных переписках их нет. */
  custom: CustomEmoji[];
  /** ↑ в пустом поле — править своё последнее, как в Discord. */
  onEditLast?: () => void;
  /** «/kick» и «/ban» — окно с подтверждением, как из меню человека. */
  onModerate?: (kind: "kick" | "ban", userId: Uuid) => void;
  /** «/msg» — отправить в личку этому человеку. */
  onDirectMessage?: (userId: Uuid, content: string) => Promise<void>;
  /** Сменился — вернуть фокус в поле: правка, начатая отсюда, кончилась. */
  focus?: number;
  /** Чей канал: Shift+Esc отмечает прочитанным весь сервер. `null` — переписка. */
  serverId?: Uuid | null;
  /** Медленный режим для меня в секундах: 0 — выключен или меня не держит. */
  slowmode?: number;
  /** Дано ли звать всех: тогда подсказка предлагает @everyone и @here. */
  canEveryone?: boolean;
  /** Стикеры сервера — вкладка рядом со знаками. В переписке их нет. */
  stickers?: ServerSticker[];
  /** Можно ли создать опрос: в ленте канала да, в ветке нет. */
  canPoll?: boolean;
  /** Голосовые сообщения — своим правом, как в Discord: файлов для них не нужно. */
  canVoiceMessage?: boolean;
  /**
   * Поле ветки: то же поле, только пишет в ветку этого корня. `threadId` —
   * `null`, пока ветку не завёл первый ответ; `title` — имя с шага «Новая
   * ветка», у заведённой — `null`.
   */
  thread?: { rootId: Uuid; threadId: Uuid | null; title: string | null };
}) {
  const store = useStore();
  const mine = useMe();
  /*
   * Черновик, срок медленного режима и файлы у ветки свои, а не канала. Ответа
   * на сообщение и «печатает» в ветке нет: и то и другое сервер знает только
   * про ленту канала.
   */
  const place = thread ? `thread:${thread.rootId}` : `channel:${channelId}`;
  const replyTo = thread ? undefined : store.replyTo(channelId);
  /*
   * «@ВКЛ» — звать ли автора того, на что отвечаем, как в Discord. Каждый новый
   * ответ начинается со «звать»: выключенное однажды не должно молча
   * переезжать на следующий ответ другому человеку.
   */
  const [ping, setPing] = useState(true);

  /*
   * Медленный режим — отсчёт до следующего сообщения. Срок держится вне
   * поля: оно пересоздаётся при смене канала, а уйти и вернуться — не способ
   * его обойти. Сервер всё равно откажет, но честнее показать срок заранее.
   */
  const [coolUntil, setCoolUntil] = useState<number | null>(() => cooldowns.get(place) ?? null);
  const [now, setNow] = useState(() => Date.now());
  const cooling = coolUntil !== null && coolUntil > now;
  useEffect(() => {
    if (coolUntil === null) return;
    const tick = window.setInterval(() => {
      const at = Date.now();
      setNow(at);
      if (at >= coolUntil) {
        cooldowns.delete(place);
        setCoolUntil(null);
      }
    }, 1000);
    return () => window.clearInterval(tick);
  }, [coolUntil, place]);

  /** Сообщение ушло — медленный режим отсчитывает срок до следующего. */
  function coolDown() {
    if (slowmode <= 0) return;
    const until = Date.now() + slowmode * 1000;
    cooldowns.set(place, until);
    setNow(Date.now());
    setCoolUntil(until);
  }
  const [pingFor, setPingFor] = useState(replyTo?.id ?? null);
  if ((replyTo?.id ?? null) !== pingFor) {
    setPingFor(replyTo?.id ?? null);
    setPing(true);
  }
  const draftPlace = place;
  /*
   * Плашки — как в Discord: упоминания, каналы, роли и метки времени видны в
   * поле именами, а уходят разметкой (`lib/pills`). Черновик — разметкой: так
   * он переживает и смену имён, и старые черновики читаются как раньше.
   */
  const names = useMemo<PillNames>(
    () => ({
      person: (handle) => {
        for (const person of people.values()) {
          if (person.handle === handle) return person.display_name || person.username;
        }
        return null;
      },
      role: (id) => roles.find((role) => role.id === id)?.name ?? null,
      channel: (id) => channels.find((channel) => channel.id === id)?.name ?? null,
    }),
    [people, roles, channels],
  );
  const [opening] = useState(() => hydrate(readDraft(mine.id, draftPlace), names));
  const [text, setText] = useState(opening.text);
  const [pills, setPills] = useState<Pill[]>(opening.pills);
  /** Плашки к этой отрисовке — подсказке, чтобы не открываться внутри плашки. */
  const pillsNow = useRef(pills);
  pillsNow.current = pills;
  const mirror = useRef<HTMLDivElement>(null);
  const [files, setFiles] = useState<QueuedFile[]>([]);
  /** Файл, которому пишут описание. */
  const [describing, setDescribing] = useState<QueuedFile | null>(null);
  const [loading, setLoading] = useState<LoadingFile[]>([]);
  const nextKey = useRef(0);
  /* Отдельного состояния не держим: «идёт ли загрузка» — это и есть непустой
     список едущих файлов, а два описания одного разошлись бы. */
  const uploading = loading.length > 0;
  useRestartBlocker(
    files.length > 0
      ? {
          id: `message-files:${place}`,
          title: "Файлы ещё не отправлены",
          detail: "отправьте сообщение или уберите вложения",
          kind: "wait",
        }
      : null,
  );

  useEffect(() => writeDraft(mine.id, draftPlace, serialize(text, pills)), [mine.id, draftPlace, text, pills]);
  useRestartPreparation(() => writeDraft(mine.id, draftPlace, serialize(text, pills)));
  /** Почему файл не взяли. Держится до следующей попытки или до закрытия. */
  const [failed, setFailed] = useState<string | null>(null);

  /*
   * Голосовое — как в Discord и как в макете v6: у пустого поля вместо
   * «отправить» — микрофон. Пока идёт запись, поле занято волной и временем,
   * «+» становится «отменить», а капля справа — «отправить». Enter отправляет,
   * Esc отменяет.
   */
  const voice = useRef<VoiceRecording | null>(null);
  const [recording, setRecording] = useState(false);
  const [, setVoiceTick] = useState(0);
  const [sendingVoice, setSendingVoice] = useState(false);
  /** Ответ в ветку в пути: ветка ждёт сервера, а не ставит в очередь. */
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!recording) return;
    const timer = window.setInterval(() => setVoiceTick((tick) => tick + 1), 100);
    return () => window.clearInterval(timer);
  }, [recording]);

  /*
   * Ушли из канала посреди записи — запись пропадает, микрофон гаснет. Запись,
   * что ещё только запускалась (ждали микрофон), гасится, когда запустится.
   */
  const mounted = useRef(true);
  const starting = useRef(false);
  const wrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      voice.current?.cancel();
      voice.current = null;
    };
  }, []);

  async function startVoice() {
    if (starting.current || voice.current) return;
    starting.current = true;
    setFailed(null);
    try {
      const taking = await recordVoice(() => void sendVoice());
      if (!mounted.current) {
        taking.cancel();
        return;
      }
      voice.current = taking;
      setRecording(true);
      // Фокус — на «отправить»: Enter и Esc записи работают, пока он в поле.
      requestAnimationFrame(() => wrap.current?.querySelector<HTMLButtonElement>(".composer-send.on")?.focus());
    } catch {
      if (mounted.current) setFailed("Нет доступа к микрофону — проверьте его в настройках");
    } finally {
      starting.current = false;
    }
  }

  function cancelVoice() {
    voice.current?.cancel();
    voice.current = null;
    setRecording(false);
  }

  async function sendVoice() {
    const taking = voice.current;
    if (!taking) return;
    voice.current = null;
    setRecording(false);
    const take = await taking.stop();
    if (!take) return;
    setSendingVoice(true);
    try {
      if (thread) await store.sendInThread("", [], thread.title, { voice: take });
      else {
        // Ответ — тот, что выбран сейчас, а не в начале записи.
        await store.sendVoice(channelId, take, store.replyTo(channelId)?.id ?? null);
        store.setReplyTo(channelId, null);
      }
      coolDown();
    } catch (error) {
      setFailed(error instanceof ApiError ? `Голосовое не ушло: ${error.message}` : "Голосовое не ушло");
    } finally {
      setSendingVoice(false);
    }
  }

  useEffect(() => {
    if (!recording) return;
    const onKey = (event: KeyboardEvent) => {
      // Клавиши — записи, только пока набор не в другом поле, окне или меню.
      const target = event.target as HTMLElement | null;
      const ours = !target || target === document.body || !!wrap.current?.contains(target);
      if (!ours || event.isComposing) return;
      if (event.key === "Escape") cancelVoice();
      else if (event.key === "Enter" && !event.shiftKey) void sendVoice();
      else return;
      event.preventDefault();
      event.stopPropagation();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
    // Обработчики читают ссылку на запись, а не состояние: пересоздавать незачем.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording]);
  /** Над окном тащат файл — показываем, куда его бросать. */
  const [over, setOver] = useState(false);
  const area = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (focus) area.current?.focus();
  }, [focus]);
  const picker = useRef<HTMLInputElement>(null);
  /*
   * Когда в последний раз сообщили, что набираем.
   *
   * В `useRef`, а не в состоянии: от этой отметки ничего не рисуется, а в
   * состоянии она будила бы отрисовку на каждый знак.
   */
  const typedAt = useRef(0);

  /*
   * Кто печатает — без себя.
   *
   * Своё событие до нас и не доходит: рассылка исключает соединение, с
   * которого пришло. Но у того же человека бывает вторая вкладка, и без
   * отсева он читал бы «вы печатаете» о самом себе.
   */
  const typing = thread ? [] : store.typingIn(channelId).filter((id) => id !== mine?.id);

  /*
   * Подсказка.
   *
   * `null` — закрыта. Иначе держим начало набранного «@…» или «:…» и то, что
   * набрано после знака: подставлять надо ровно этот кусок, а не последнее
   * слово вообще — иначе почта вроде «me@example.com» превратится в чужое имя.
   */
  const [hint, setHint] = useState<Hint | null>(null);
  const [chosen, setChosen] = useState(0);

  /*
   * Команды ботов сервера — в подсказке «/» и карточкой параметров над полем,
   * пока команду набирают. Список просим, когда поле начали с «/».
   */
  const [appCommands, setAppCommands] = useState<ChannelCommand[]>(
    () => commandCache.get(channelId)?.items ?? [],
  );
  // В ветке команды ботов пока не вызываются: вызов ушёл бы в ленту канала.
  const apps = !!serverId && !thread;
  const wantsCommands = apps && text.startsWith("/");
  useEffect(() => {
    if (!wantsCommands) return;
    const cached = commandCache.get(channelId);
    if (cached && Date.now() - cached.at < COMMANDS_FRESH) {
      setAppCommands(cached.items);
      return;
    }
    let live = true;
    api.commands
      .list(channelId)
      .then(({ items }) => {
        commandCache.set(channelId, { at: Date.now(), items });
        if (live) setAppCommands(items);
      })
      // Нет списка — нет и подсказки ботов; встроенные команды работают без него.
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [channelId, wantsCommands]);
  // Ответ быстрого бота может прийти раньше, чем вернётся сам вызов: слушать — заранее.
  useEffect(() => listenCalls(), []);
  const typedApp = useMemo(() => (apps ? typedCommand(text, appCommands) : null), [apps, text, appCommands]);

  /*
   * Автодополнение — как в Discord: параметр в фокусе с `autocomplete` спрашивает
   * варианты у бота (вызов типа 4), они приходят событием и встают в подсказку.
   * Спрашиваем, когда набор замер на четверть секунды, а не на каждую букву.
   */
  const asked = useMemo(
    () =>
      typedApp
        ? autocompleteOf(text, typedApp, {
            person: (handle) =>
              [...people.values()].find((person) => person.handle.toLowerCase() === handle.toLowerCase())?.id ?? null,
          })
        : null,
    [text, typedApp, people],
  );
  const askKey = asked && typedApp ? `${typedApp.command.id}:${JSON.stringify(asked.values)}` : "";
  const [askId, setAskId] = useState<Uuid | null>(null);
  useEffect(() => {
    if (!asked || !typedApp) {
      setAskId(null);
      return;
    }
    let live = true;
    const timer = window.setTimeout(() => {
      api.commands
        .autocomplete(channelId, typedApp.command.id, asked.values)
        .then(({ id }) => live && setAskId(id))
        .catch(() => live && setAskId(null));
    }, AUTOCOMPLETE_PAUSE);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
    // Спрашиваем по смыслу набранного, а не по каждому новому объекту разбора.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askKey, channelId]);
  const choices = useInteractions((state) => (askId ? state.choices[askId] : undefined));
  useEffect(() => setChosen(0), [choices]);

  /** Открыта ли панель «Гифки, стикеры, эмодзи» и на какой вкладке. */
  const [picking, setPicking] = useState(false);
  const [pickTab, setPickTab] = useState<ExpressionTab>("emoji");
  /** Кнопка поля открывает панель на своей вкладке; повторное нажатие той же — закрывает. */
  function togglePick(tab: ExpressionTab) {
    setPicking(!(picking && pickTab === tab));
    setPickTab(tab);
  }
  const stickerServer = useCatalogue((state) => (serverId ? (state.servers.find((item) => item.id === serverId) ?? null) : null));
  /** «+» — как в Discord: меню «Загрузить файл», «Создать опрос» (не в ветке) и «Вставить время». */
  const [plusAt, setPlusAt] = useState<{ x: number; y: number } | null>(null);
  const [polling, setPolling] = useState(false);
  const [stamping, setStamping] = useState(false);

  /*
   * Стикер уходит сразу, отдельным сообщением — как в Discord: набранный
   * текст остаётся в поле. Медленный режим и права проверяет сервер; его
   * отказ — строкой под полем.
   */
  async function sendSticker(id: Uuid) {
    setPicking(false);
    setFailed(null);
    try {
      if (thread) await store.sendInThread("", [], thread.title, { stickerId: id });
      else await store.sendSticker(channelId, id);
    } catch (error) {
      setFailed(error instanceof ApiError ? `Стикер не ушёл: ${error.message}` : "Стикер не ушёл");
    }
  }
  /*
   * Фокус и рост поля — для капель (v6): в фокусе или с набранным «+», поле
   * и «отправить» стекаются в одну форму, а высота капель идёт за полем.
   */
  const [focused, setFocused] = useState(false);
  const [rowH, setRowH] = useState(ROW);

  /*
   * Высота капель — за полем: `grow()` тянет его до пяти строк, капли растут
   * следом. Сменилась ширина (открыли колонку справа, потянули окно) — строки
   * переносятся заново, и высоту поля надо пересчитать тут же, а не ждать
   * следующей буквы. Пересчёт тот же, что в `grow()`; новая высота вызовет
   * наблюдателя ещё раз, и на нём всё сойдётся.
   */
  useEffect(() => {
    const element = area.current;
    if (!element) return;
    const ro = new ResizeObserver(() => {
      const line = Number.parseFloat(getComputedStyle(element).lineHeight) || 21;
      element.style.height = "auto";
      element.style.height = `${Math.min(line * 5 + 20, element.scrollHeight)}px`;
      setRowH(Math.max(ROW, Math.round(element.offsetHeight) + ROW - 21));
    });
    ro.observe(element);
    return () => ro.disconnect();
  }, [disabled]);

  /*
   * Набор для подсказки по «:».
   *
   * Просим его, только когда подсказка открылась: до первого двоеточия он не
   * нужен, а весит четверть мегабайта. Пока не приехал, подсказка показывает
   * знаки сервера — их список уже под рукой.
   */
  const set = useEmojiSet(hint?.kind === "emoji");


  // Нажали «ответить» — курсор должен быть уже в поле, без второго клика.
  useEffect(() => {
    if (replyTo) area.current?.focus();
    /*
     * Только на смену того, на что отвечаем: сама цитата приезжает объектом
     * сообщения и меняется от правки, реакции, прихода ветки — забирать фокус
     * на каждое такое изменение значит отбирать его у человека посреди набора.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replyTo?.id]);

  /** Что показывать под набранным. Пятёрки хватает, список не экран. */
  const suggestions: Suggestion[] = useMemo(() => {
    if (!hint) {
      return asked && choices
        ? choices.map((choice) => ({ kind: "choice", choice, start: asked.start }) as const)
        : [];
    }

    if (hint.kind === "command") {
      const query = hint.query.toLowerCase();
      const fromBots: Suggestion[] = (apps ? appCommands : [])
        .filter((command) => command.type === 1 && command.name.startsWith(query))
        .slice(0, 10)
        .map((command) => ({ kind: "app", command }) as const);
      return [
        ...fromBots,
        ...COMMANDS.filter((command) => command.name.startsWith(query) && (!command.serverOnly || !!serverId)).map(
          (command) => ({ kind: "command", command }) as const,
        ),
      ];
    }

    if (hint.kind === "channel") {
      const query = hint.query.toLowerCase();

      return channels
        .filter((item) => item.name.toLowerCase().includes(query))
        .slice(0, 5)
        .map((channel) => ({ kind: "channel", channel }) as const);
    }

    if (hint.kind === "mention") {
      const query = hint.query.toLowerCase();

      const named: Suggestion[] = [...people.values()]
        .filter(
          (person) =>
            person.username.toLowerCase().startsWith(query) ||
            (person.display_name ?? "").toLowerCase().startsWith(query),
        )
        .slice(0, 5)
        .map((person) => ({ kind: "person", person }) as const);

      /*
       * Роли — после людей и не вместо них.
       *
       * Одной «собакой», а не своим знаком: зовут ими одно и то же — внимание
       * человека, — и помнить, каким знаком зовётся группа, никому не надо.
       *
       * Роль по умолчанию в списке не появится: её отсеивает тот, кто
       * собирает список ролей. Позвать её значит позвать весь сервер, а это
       * отдельное право, которого пока нет.
       */
      const grouped: Suggestion[] = roles
        .filter((role) => role.name.toLowerCase().startsWith(query))
        .slice(0, 5 - Math.min(named.length, 3))
        .map((role) => ({ kind: "role", role }) as const);

      // @everyone и @here — только тем, кому дано звать всех: остальным
      // сервер их не засчитает, и подсказка обещала бы несбыточное.
      const all: Suggestion[] = canEveryone
        ? (["everyone", "here"] as const)
            .filter((word) => word.startsWith(query))
            .map((word) => ({ kind: "everyone", word }) as const)
        : [];

      return [...named, ...grouped, ...all].slice(0, 7);
    }

    // Свои знаки идут первыми: их заводили руками и по имени помнят точнее.
    const own: Suggestion[] = searchCustom(custom, hint.query)
      .slice(0, 3)
      .map((item) => ({
        kind: "emoji",
        insert: `:${item.name}:`,
        mark: "",
        custom: item,
        token: `:${item.name}:`,
      }));

    const rest: Suggestion[] = set
      ? searchEmoji(set, hint.query, 6 - own.length).map((row) => ({
          kind: "emoji",
          insert: withTone(row, skinTone()),
          mark: withTone(row, skinTone()),
          custom: null,
          token: `:${row[2][0]}:`,
        }))
      : [];

    return [...own, ...rest];
  }, [hint, people, roles, custom, channels, set, canEveryone, serverId, apps, appCommands, asked, choices]);

  /*
   * Подсказку сняли Esc'ом, и до следующего ввода она не возвращается.
   *
   * Без этого признака Esc не работал вовсе: `look` зовётся и на отпускание
   * клавиши, а отпускают в том числе сам Esc — подсказка закрывалась на
   * нажатии и открывалась обратно через миг, на том же нажатии.
   *
   * Возвращает её именно ввод, а не движение каретки. Стрелки, щелчок мышью
   * и возврат в поле оставляют её снятой: человек сказал, что сейчас она ему
   * не нужна, и ходить по строке из-за этого нельзя было бы.
   *
   * В ссылке, а не в состоянии: от неё ничего не рисуется — она только
   * решает, слушать ли `look`, — а в состоянии она давала бы лишнюю
   * отрисовку на каждое нажатие.
   */
  const dismissed = useRef(false);

  /**
   * Ищет незакрытое «@…» или «:…» слева от курсора.
   *
   * Знак считается началом только в начале строки или после пробела: иначе в
   * подсказку попадала бы почта, а в подсказку знаков — время.
   *
   * Побеждает тот, что ближе к курсору: «@аня :гор» — это про знак, хотя имя
   * левее и тоже не закрыто.
   */
  function look(value: string, caret: number) {
    if (dismissed.current) return;
    // Курсор в плашке или сразу за ней — это уже выбранное, подсказывать нечего.
    if (pillAt(pillsNow.current, caret)) {
      setHint(null);
      return;
    }

    const upto = value.slice(0, caret);
    const found: Hint[] = [];

    const at = upto.lastIndexOf("@");
    if (at >= 0 && (at === 0 || /\s/.test(upto[at - 1]))) {
      const query = upto.slice(at + 1);
      if (!/[\s@]/.test(query)) found.push({ kind: "mention", at, query });
    }

    /*
     * Канал. Решётка берётся только с начала слова — иначе подсказка лезла бы
     * в середину хэндла (`@аня#0042`) и в любой номер вроде «дом#12».
     *
     * Пустой запрос допустим: сразу после решётки показывается весь список, и
     * это удобнее, чем требовать первую букву у имени, которого не помнишь.
     */
    const hash = upto.lastIndexOf("#");
    if (hash >= 0 && (hash === 0 || /\s/.test(upto[hash - 1]))) {
      const query = upto.slice(hash + 1);
      if (!/[\s#]/.test(query)) found.push({ kind: "channel", at: hash, query });
    }

    const colon = upto.lastIndexOf(":");
    if (colon >= 0 && (colon === 0 || /\s/.test(upto[colon - 1]))) {
      const query = upto.slice(colon + 1);
      if (query.length >= EMOJI_HINT_MIN && !/[\s:]/.test(query)) {
        found.push({ kind: "emoji", at: colon, query });
      }
    }

    // Команда — только в самом начале поля и пока не начат её текст, как в Discord.
    const slash = /^\/([^\s/]*)$/.exec(upto);
    if (slash) found.push({ kind: "command", at: 0, query: slash[1] });

    const nearest = found.sort((left, right) => right.at - left.at)[0] ?? null;

    if (!nearest) return setHint(null);

    /*
     * Отметку сбрасываем, только когда подсказка стала другой.
     *
     * `look` зовётся и на отпускание клавиши — в том числе стрелки, которой по
     * подсказке и ходят. Безусловный сброс возвращал выбор на первую строку
     * сразу после того, как стрелка его сдвинула: нажатие двигало, отпускание
     * возвращало, и выбрать кого-либо, кроме первого, было нельзя.
     *
     * Сравнение по всей подсказке, а не по одному запросу: стрелки вбок
     * двигают каретку, и тогда сброс как раз нужен — набранное «@ан» на другом
     * месте строки это другой список.
     */
    const same =
      hint !== null &&
      hint.kind === nearest.kind &&
      hint.at === nearest.at &&
      hint.query === nearest.query;

    if (!same) setChosen(0);

    setHint(nearest);
  }

  /** Подставляет выбранное вместо набранного куска. */
  function put(item: Suggestion) {
    // Вариант бота встаёт на место набранного значения — значением, а не именем.
    if (item.kind === "choice") {
      const next = `${text.slice(0, item.start)}${String(item.choice.value)} `;
      setText(next);
      setAskId(null);
      requestAnimationFrame(() => {
        area.current?.focus();
        area.current?.setSelectionRange(next.length, next.length);
        grow();
      });
      return;
    }
    if (!hint) return;

    /*
     * Хэндл подставляется целиком: по нему упоминание и разбирается сервером.
     * Канал — номером в скобках: имя меняют, и по имени упоминание однажды
     * указало бы в никуда.
     */
    // Команда бота сразу зовёт свой первый обязательный параметр, как плашка в Discord.
    const lead = item.kind === "app" ? item.command.options.find((option) => option.required) : undefined;
    const body =
      item.kind === "command"
        ? `/${item.command.name}`
        : item.kind === "app"
        ? `/${item.command.name}${lead && lead.type !== OPTION.sub && lead.type !== OPTION.group ? ` ${lead.name}:` : ""}`
        : item.kind === "person"
        ? `@${item.person.handle}`
        : item.kind === "everyone"
          ? `@${item.word}`
          : item.kind === "role"
          ? `<@&${item.role.id}>`
          : item.kind === "channel"
            ? `<#${item.channel.id}>`
            : item.insert;
    // В поле — имя, при отправке — разметка выше.
    const shown =
      item.kind === "person"
        ? `@${item.person.display_name || item.person.username}`
        : item.kind === "role"
          ? `@${item.role.name}`
          : item.kind === "channel"
            ? `#${item.channel.name}`
            : body;
    const pilled = item.kind === "person" || item.kind === "role" || item.kind === "channel" || item.kind === "everyone";
    const caret = hint.at + hint.query.length + 1;
    const next = `${text.slice(0, hint.at)}${shown} ${text.slice(caret)}`;
    const moved = rebase(pills, text, next);

    setText(next);
    setPills(pilled ? [...moved, { start: hint.at, end: hint.at + shown.length, raw: body }] : moved);
    setHint(null);
    requestAnimationFrame(() => {
      const place = hint.at + shown.length + 1;
      area.current?.focus();
      area.current?.setSelectionRange(place, place);
      grow();
    });
  }

  /*
   * Команды ботов к отправке: из запаса, а нет его — спросить сейчас. Вставили
   * «/команду» и сразу нажали Enter — список ещё в пути, и без ожидания вызов
   * ушёл бы в канал текстом.
   */
  async function botCommands(): Promise<ChannelCommand[]> {
    const cached = commandCache.get(channelId);
    if (cached) return cached.items;
    try {
      const { items } = await api.commands.list(channelId);
      commandCache.set(channelId, { at: Date.now(), items });
      setAppCommands(items);
      return items;
    } catch {
      return appCommands;
    }
  }

  /** Параметр из карточки команды — в конец набранного: «имя:», у подкоманды — само слово. */
  function addOption(option: CommandOption) {
    const word = option.type === OPTION.sub || option.type === OPTION.group ? `${option.name} ` : `${option.name}:`;
    const next = `${text.replace(/\s*$/, " ")}${word}`;
    setText(next);
    requestAnimationFrame(() => {
      area.current?.focus();
      area.current?.setSelectionRange(next.length, next.length);
      grow();
    });
  }

  /** Кладёт знак туда, где стоит курсор. Панель закрывается сама. */
  function insert(value: string) {
    const element = area.current;
    const caret = element?.selectionStart ?? text.length;
    const until = element?.selectionEnd ?? caret;
    const shown = hydrate(value, names);
    const next = `${text.slice(0, caret)}${shown.text}${text.slice(until)}`;

    setText(next);
    setPills([
      ...rebase(pills, text, next),
      ...shown.pills.map((pill) => ({ ...pill, start: pill.start + caret, end: pill.end + caret })),
    ]);
    setPicking(false);
    requestAnimationFrame(() => {
      const place = caret + shown.text.length;
      element?.focus();
      element?.setSelectionRange(place, place);
      grow();
    });
  }

  /**
   * Поле растёт до пяти строк, дальше прокручивается — так решено в макете.
   *
   * Пять, а не «сколько влезет»: размеченное сообщение бывает длинным, и поле
   * во весь экран съедает ленту, ради которой человек тут и сидит.
   */
  function grow() {
    const element = area.current;
    if (!element) return;

    const line = Number.parseFloat(getComputedStyle(element).lineHeight) || 21;

    element.style.height = "auto";
    element.style.height = `${Math.min(line * 5 + 20, element.scrollHeight)}px`;
    syncMirror();
  }

  /**
   * Зеркало плашек и подсказки — ровно под полем: тот же прямоугольник, шрифт
   * и отступы, ширина — без полосы прокрутки, иначе строки переносились бы
   * по-разному.
   */
  function syncMirror() {
    const field = area.current;
    const copy = mirror.current;
    if (!field || !copy) return;
    const style = getComputedStyle(field);
    copy.style.left = `${field.offsetLeft}px`;
    copy.style.top = `${field.offsetTop}px`;
    copy.style.width = `${field.clientWidth}px`;
    copy.style.height = `${field.clientHeight}px`;
    copy.style.font = style.font;
    copy.style.letterSpacing = style.letterSpacing;
    copy.style.lineHeight = style.lineHeight;
    copy.style.padding = style.padding;
    copy.scrollTop = field.scrollTop;
  }
  // Каждую отрисовку: текст, плашки и размер поля меняются вместе.
  useLayoutEffect(syncMirror);

  /** Сообщает, что набирают, — но не чаще, чем раз в `TYPING_EVERY`. */
  function announceTyping() {
    if (thread) return;
    const now = Date.now();
    if (now - typedAt.current < TYPING_EVERY) return;

    typedAt.current = now;

    /*
     * Отказ проглатываем молча. Надпись у собеседника — не то, ради чего
     * стоит тревожить того, кто печатает: не загорелась и не загорелась.
     */
    void api.messages.typing(channelId).catch(() => {});
  }

  async function submit() {
    let content = serialize(text, pills).trim();
    if (!content && files.length === 0) return;
    if (cooling) return;

    /*
     * «s/было/стало» — правка своего последнего сообщения, как в Discord.
     * Нет такого слова в нём — подсказка, а не отправка: случайная «s/…» в
     * ленте выглядела бы опечаткой.
     */
    const swap = files.length === 0 ? quickEdit(content) : null;
    if (swap) {
      // У ветки — её ответы: они всегда загружены до последнего.
      const said = thread ? (thread.threadId ? store.threadMessages(thread.threadId) : undefined) : store.messages(channelId);
      const last = (said ?? [])
        .filter((item) => item.author.id === mine.id && !item.kind && !item.attachments.some((file) => file.waveform))
        .at(-1);
      if (last && !thread && !store.atLatest(channelId)) {
        // Окно уехало от конца — «последнее своё» в нём может оказаться старым.
        setFailed("Вернитесь к последним сообщениям, чтобы править через s/…/…/");
        return;
      }
      if (last) {
        if (!last.content.includes(swap.from)) {
          setFailed(`В последнем сообщении нет «${swap.from}»`);
          return;
        }
        try {
          // Функцией, а не строкой: иначе «$&» и «$$» в замене читались бы как шаблоны.
          await store.edit(channelId, last.id, last.content.replace(swap.from, () => swap.to));
          setText("");
          setPills([]);
          writeDraft(mine.id, draftPlace, "");
          setFailed(null);
          requestAnimationFrame(grow);
        } catch (error) {
          setFailed(error instanceof ApiError ? `Правка не сохранилась: ${error.message}` : "Правка не сохранилась");
        }
        return;
      }
    }

    /*
     * Команда бота — вызов, а не сообщение, как в Discord: в канал попадёт ответ
     * бота, а над ним — кто и что вызвал. Параметры проверяются здесь же, чтобы
     * ошибка была понятной, а не отказом сервера.
     */
    const app =
      apps && files.length === 0 && content.startsWith("/") ? typedCommand(content, await botCommands()) : null;
    if (app) {
      const parsed = parseCommand(app, {
        person: (handle) =>
          [...people.values()].find((person) => person.handle.toLowerCase() === handle.toLowerCase())?.id ?? null,
      });
      if (!parsed.ok) {
        setFailed(parsed.error);
        return;
      }
      try {
        const call = await api.commands.invoke(channelId, app.command.id, parsed.options);
        useInteractions.getState().sent({ id: call.id, channelId, name: app.command.name, bot: app.command.bot });
        setText("");
        setPills([]);
        writeDraft(mine.id, draftPlace, "");
        setFailed(null);
        requestAnimationFrame(grow);
      } catch (error) {
        setFailed(error instanceof ApiError ? `Команда не вызвана: ${error.message}` : "Команда не вызвана");
      }
      return;
    }

    // Встроенные команды: «/nick» меняет ник, остальные дают готовый текст.
    const command = runCommand(content, !!serverId);
    if (command?.kind === "nick") {
      try {
        await api.servers.nickname(serverId!, mine.id, command.nick);
        setText("");
        setPills([]);
        writeDraft(mine.id, draftPlace, "");
        requestAnimationFrame(grow);
      } catch (error) {
        setFailed(error instanceof ApiError ? `Ник не сменился: ${error.message}` : "Ник не сменился");
      }
      return;
    }
    /*
     * «/msg», «/kick», «/ban», «/timeout» — как в Discord: кому — хэндлом из
     * подсказки «@». Выгнать и забанить спрашивают подтверждение тем же окном,
     * что меню человека; тайм-аут и личка — сразу.
     */
    if (command?.kind === "usage") {
      setFailed(command.text);
      return;
    }
    if (command && command.kind !== "send") {
      const wanted = command.handle.toLowerCase();
      const who = [...people.values()].find((person) => person.handle.toLowerCase() === wanted);
      if (!who) {
        setFailed(`Не знаю, кто это: @${command.handle}`);
        return;
      }
      try {
        if (command.kind === "msg") await onDirectMessage?.(who.id, command.content);
        else if (command.kind === "timeout") await api.bans.timeout(serverId!, who.id, command.minutes);
        else onModerate?.(command.kind, who.id);
        setText("");
        setPills([]);
        writeDraft(mine.id, draftPlace, "");
        setFailed(null);
        requestAnimationFrame(grow);
      } catch (error) {
        const what = command.kind === "msg" ? "Личка не открылась" : "Тайм-аут не выставлен";
        setFailed(error instanceof ApiError ? `${what}: ${error.message}` : what);
      }
      return;
    }
    if (command) content = command.content;

    /*
     * Пока файлы едут, не отправляем: сообщение ушло бы без них, а человек
     * увидел бы это только у себя в ленте. Полоска рядом объясняет, чего ждём.
     */
    if (loading.length > 0) return;

    const spoilerIds = files.filter((file) => file.spoiler).map((file) => file.id);
    const descriptions = files.flatMap((file) => (file.description ? [{ id: file.id, text: file.description }] : []));

    /*
     * В ветку — дожидаясь ответа сервера, как и прежнее поле ветки: очереди с
     * повтором у неё нет, и набранное остаётся в поле, пока ответ не ушёл.
     */
    if (thread) {
      if (sending) return;
      let releaseRestart: () => void;
      try {
        releaseRestart = holdRestartBlocker({
          id: `thread-send:${thread.rootId}`,
          title: "Ответ отправляется",
          detail: "дождитесь подтверждения сервера",
          kind: "wait",
        });
      } catch {
        return;
      }
      setSending(true);
      setFailed(null);
      try {
        await store.sendInThread(content, files.map((file) => file.id), thread.title, { spoilerIds, descriptions });
        coolDown();
        setText("");
        setPills([]);
        writeDraft(mine.id, draftPlace, "");
        setFiles([]);
        requestAnimationFrame(grow);
      } catch (error) {
        // Ответ не ушёл — сказать почему, а не молча оставить текст в поле.
        setFailed(
          error instanceof ApiError && error.code === "thread_locked"
            ? "Ветка заблокирована — отвечать в ней больше нельзя"
            : (refusal(error) ?? (error instanceof ApiError ? error.message : "Ответ не ушёл")),
        );
      } finally {
        releaseRestart();
        setSending(false);
      }
      return;
    }

    const answering = replyTo?.id ?? null;
    let sendFlight: Promise<void>;

    try {
      // `send` синхронно принимает сообщение в локальную очередь. Если updater
      // уже начал передачу управления установщику, отказ случится здесь, пока
      // текст, файлы и выбранный ответ ещё не тронуты.
      sendFlight = store.send(channelId, content, files.map((file) => file.id), answering, ping, spoilerIds, descriptions);
    } catch {
      return;
    }

    coolDown();

    setText("");
    setPills([]);
    writeDraft(mine.id, draftPlace, "");
    setFiles([]);
    store.setReplyTo(channelId, null);
    requestAnimationFrame(grow);

    await sendFlight;
  }

  /**
   * Кладёт файлы в очередь отправки.
   *
   * Один путь на все три способа — кнопка, Ctrl+V и бросок в окно. Разными
   * они бывают только снаружи: дальше это один и тот же список файлов.
   */
  async function pick(list: FileList | File[] | null) {
    const chosen = list ? Array.from(list) : [];
    if (chosen.length === 0) return;

    setFailed(null);

    try {
      for (const file of chosen) {
        /*
         * Размер смотрим до отправки. Сервер откажет и сам, но к тому мигу
         * файл уже уедет целиком — на полусотне мегабайт это минута ожидания
         * ради заведомого отказа.
         */
        if (file.size > ATTACHMENT_MAX) {
          setFailed(
            `«${file.name}» тяжелее ${Math.round(ATTACHMENT_MAX / 1024 / 1024)} МБ — не возьмём`,
          );
          continue;
        }

        /*
         * В очередь файл встаёт до отправки, а не после.
         *
         * Раньше он появлялся только готовым, и между нажатием и появлением
         * не было ничего — на большом файле это секунды тишины, по которым не
         * понять, идёт что-то или нет.
         */
        const key = nextKey.current;
        nextKey.current += 1;

        let releaseRestart: () => void;
        try {
          releaseRestart = holdRestartBlocker({
            id: `message-file-upload:${place}:${key}`,
            title: "Загружается файл",
            detail: "дождитесь окончания загрузки",
            kind: "wait",
          });
        } catch (error) {
          setFailed(error instanceof Error ? error.message : "Airel уже перезапускается");
          continue;
        }
        setLoading((previous) => [
          ...previous,
          { key, name: file.name, sent: 0, total: file.size },
        ]);

        try {
          const meta = await api.files.upload(file, "attachment", (sent, total) => {
            setLoading((previous) =>
              previous.map((item) => (item.key === key ? { ...item, sent, total } : item)),
            );
          });

          setFiles((previous) => [...previous, { id: meta.id, name: meta.filename }]);
        } catch (error) {
          /*
           * Отказ по одному файлу не отменяет остальные: бросают пачкой, и
           * потерять её целиком из-за одного хуже, чем про него сказать.
           *
           * Раньше отказа не было видно вовсе — файл просто не появлялся в
           * очереди, и это выглядело как «нажал и ничего».
           */
          setFailed(
            error instanceof Error ? error.message : `«${file.name}» не загрузился`,
          );
        } finally {
          releaseRestart();
          setLoading((previous) => previous.filter((item) => item.key !== key));
        }
      }
    } finally {
      if (picker.current) picker.current.value = "";
    }
  }

  const currentPick = useRef(pick);
  currentPick.current = pick;

  /*
   * Файл, брошенный куда угодно в окне, попадает сюда.
   *
   * Слушатели на окне, а не на поле ввода: целиться в полоску внизу — лишняя
   * работа, и ни один знакомый людям чат так не делает. Запрет на своих
   * слушателей из CLAUDE.md сюда не относится: он про очередь Esc у
   * всплывающих слоёв, а здесь ни фокуса, ни закрытия нет.
   *
   * Вход и выход считаются, а не держатся признаком: `dragenter` приходит и от
   * каждого дочернего узла, и без счёта подсветка мигала бы на каждой границе
   * внутри окна.
   *
   * Своё перетаскивание отсеиваем по `Files`: каналы переставляют тем же
   * механизмом, и без проверки подсветка вспыхивала бы при каждой перестановке.
   */
  useEffect(() => {
    /*
     * Слушатели стоят всегда, даже когда прикладывать нечего.
     *
     * Умолчание браузера на брошенный файл — открыть его вместо страницы. В
     * приложении это уводит окно с него совсем, и вернуться можно только
     * перезапуском. Поэтому бросок мы отбиваем в любом случае, а берём файл
     * только когда есть право и поле открыто.
     */
    const takes = canUpload && !disabled;

    /*
     * Полей бывает два — лента и ветка в колонке. Файл берёт то, над чьей
     * областью его держат: колонка ветки помечена `data-drop-zone`, всё
     * остальное окно — поля ленты. Без этого брошенный файл прикладывался к
     * обоим сразу.
     */
    const zoneOf = (node: EventTarget | null) =>
      node instanceof Element ? node.closest("[data-drop-zone]") : null;
    const ours = (event: DragEvent) => zoneOf(event.target) === zoneOf(wrap.current);

    let depth = 0;

    const carriesFiles = (event: DragEvent) =>
      Array.from(event.dataTransfer?.types ?? []).includes("Files");

    const enter = (event: DragEvent) => {
      if (!carriesFiles(event) || !takes) return;
      depth += 1;
      setOver(ours(event));
    };

    const move = (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      // Без этого браузер бросок не разрешит и покажет знак запрета.
      event.preventDefault();
      // Файл перевели из ленты в колонку ветки и обратно — подсказка переходит следом.
      if (takes) setOver(ours(event));
    };

    const leave = (event: DragEvent) => {
      if (!carriesFiles(event) || !takes) return;
      depth -= 1;
      if (depth > 0) return;

      depth = 0;
      setOver(false);
    };

    const land = (event: DragEvent) => {
      if (!carriesFiles(event)) return;

      // Отбиваем всегда — см. выше про уход окна с страницы.
      event.preventDefault();

      if (!takes) return;

      depth = 0;
      setOver(false);
      if (ours(event)) void currentPick.current(event.dataTransfer?.files ?? null);
    };

    window.addEventListener("dragenter", enter);
    window.addEventListener("dragover", move);
    window.addEventListener("dragleave", leave);
    window.addEventListener("drop", land);

    return () => {
      window.removeEventListener("dragenter", enter);
      window.removeEventListener("dragover", move);
      window.removeEventListener("dragleave", leave);
      window.removeEventListener("drop", land);
    };
    /*
     * Свежий `pick` лежит в ссылке: он знает текущий канал, но его новое
     * тождество не заставляет переподписывать четыре оконных слушателя на
     * каждую букву.
     */
  }, [canUpload, disabled]);

  /*
   * После блокировки закрывается только ввод: история остаётся читаемой
   * обоим. Стирать её нечестно — и сервер её не стирает.
   */
  if (disabled) {
    return (
      <div className="composer-wrap pane-surface">
        <div className="composer-blocked">
          <span className="grow">{disabledReason}</span>
          {onUnblock && (
            <button type="button" className="link-btn" onClick={onUnblock}>
              Разблокировать
            </button>
          )}
        </div>
      </div>
    );
  }

  const ready = (!!text.trim() || files.length > 0) && loading.length === 0 && !cooling && !sending;
  const merged = focused || !!text.trim() || files.length > 0 || loading.length > 0 || recording;
  const canVoice = canVoiceMessage && !cooling && files.length === 0 && loading.length === 0 && !text.trim();

  return (
    <div className="composer-wrap" ref={wrap}>
      {/*
        Полотно на всё окно, а не на поле ввода: бросают куда попало, и
        подсказка должна стоять там же, где взгляд.

        Обычный `position: fixed`, а не слой Radix: закрывать его нечем и
        незачем — оно живёт ровно пока над окном держат файл, фокуса не берёт
        и в очереди Esc не участвует.
      */}
      {/*
        Вынесено из панели, и это обязательно. Отсчёт у `position: fixed` не
        всегда от окна: элемент с `backdrop-filter` становится для таких детей
        началом отсчёта — так же, как с `transform`. Панель поля ввода
        размытая, и полотно съёжилось до её полоски: 753×66 вместо 1375×918.
        По разметке этого не видно, только замером.
      */}
      {over &&
        createPortal(
          <div className="drop-veil">
            <div className="drop-veil-box">
              <span className="drop-veil-sign">+</span>
              {thread ? "Отпустите — приложим к ответу в ветке" : "Отпустите — приложим к сообщению"}
            </div>
          </div>,
          document.body,
        )}

      {/*
        Ошибка, ответ и очередь файлов — отдельной стеклянной карточкой над
        каплями, как лоток вложений в макете: внутри капли поля им места нет.
      */}
      {(failed || replyTo || files.length > 0 || loading.length > 0 || cooling) && (
      <div className="composer-tray glass-host">
      {cooling && (
        <div className="composer-reply composer-slow">
          <IconTimer size={13} />
          <span className="grow">Медленный режим: следующее сообщение через {left(coolUntil! - now)}</span>
        </div>
      )}
      {failed && (
        <div className="composer-failed">
          <span className="grow">{failed}</span>
          <button type="button" aria-label="Скрыть" onClick={() => setFailed(null)}>
            <IconClose size={12} />
          </button>
        </div>
      )}

      {replyTo && (
        <div className="composer-reply">
          <span className="composer-reply-turn"><IconQuoted size={13} /></span>
          <span className="composer-reply-name">
            {replyTo.author.display_name || replyTo.author.username}
          </span>
          <span className="composer-reply-text ellipsis grow">{replyTo.content}</span>
          {/* Себе ответ не зовёт никого — и переключать нечего. */}
          {replyTo.author.id !== mine?.id && (
            <button
              type="button"
              className={`composer-reply-ping ${ping ? "on" : ""}`}
              aria-pressed={ping}
              title={ping ? "Автор получит оповещение — нажмите, чтобы не звать" : "Автор не получит оповещения"}
              onClick={() => setPing((value) => !value)}
            >
              @{ping ? "ВКЛ" : "ВЫКЛ"}
            </button>
          )}
          <button
            type="button"
            aria-label="Не отвечать"
            title="Не отвечать · Esc"
            onClick={() => store.setReplyTo(channelId, null)}
          >
            <IconClose size={13} />
          </button>
        </div>
      )}

      {(files.length > 0 || loading.length > 0) && (
        <div className="composer-queue">
          {loading.map((item) => {
            /*
             * Доля считается от `total`, который присылает сам браузер, а не
             * от размера файла: у составного тела к файлу добавляются
             * заголовки частей, и по размеру файла полоска не доходила бы до
             * конца.
             */
            const share = item.total > 0 ? Math.min(1, item.sent / item.total) : 0;
            const percent = Math.round(share * 100);

            return (
              <span
                key={item.key}
                className="queued-file loading"
                role="progressbar"
                aria-label={`Загрузка ${item.name}`}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={percent}
              >
                <Spinner />
                <span className="ellipsis">{item.name}</span>
                <span className="queued-share">{percent}%</span>
                <span className="queued-bar" aria-hidden="true">
                  <span className="queued-bar-fill" style={{ width: `${percent}%` }} />
                </span>
              </span>
            );
          })}

          {files.map((file) => (
            <span key={file.id} className={`queued-file ${file.spoiler ? "spoiler" : ""}`}>
              {file.name}
              <button
                type="button"
                aria-label={file.spoiler ? `Снять спойлер с ${file.name}` : `Пометить ${file.name} как спойлер`}
                aria-pressed={!!file.spoiler}
                title={file.spoiler ? "Спойлер: закрыто у всех, пока не нажмут" : "Пометить как спойлер"}
                onClick={() =>
                  setFiles((previous) =>
                    previous.map((item) => (item.id === file.id ? { ...item, spoiler: !item.spoiler } : item)),
                  )
                }
              >
                {file.spoiler ? <IconEyeOff size={12} /> : <IconEye size={12} />}
              </button>
              <button
                type="button"
                className={`queued-alt ${file.description ? "on" : ""}`}
                aria-label={`Описание: ${file.name}`}
                title={file.description ? `Описание: ${file.description}` : "Добавить описание — для читалки экрана"}
                onClick={() => setDescribing(file)}
              >
                ALT
              </button>
              <button
                type="button"
                aria-label={`Убрать ${file.name}`}
                onClick={() => setFiles((previous) => previous.filter((item) => item.id !== file.id))}
              >
                <IconClose size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      </div>
      )}

      {canUpload && (
        <input ref={picker} type="file" multiple hidden onChange={(event) => void pick(event.target.files)} />
      )}

      {describing && (
        <DescribeDialog
          file={describing}
          onSave={(description) => {
            setFiles((previous) =>
              previous.map((item) => (item.id === describing.id ? { ...item, description } : item)),
            );
            setDescribing(null);
          }}
          onClose={() => setDescribing(null)}
        />
      )}

      {plusAt && (
        <Menu
          title="ДОБАВИТЬ"
          at={plusAt}
          onClose={() => setPlusAt(null)}
          items={[
            ...(canUpload ? [{ label: "Загрузить файл", onPick: () => picker.current?.click() }] : []),
            ...(canPoll ? [{ label: "Создать опрос", onPick: () => setPolling(true) }] : []),
            { label: "Вставить время", onPick: () => setStamping(true) },
          ]}
        />
      )}
      {polling && (
        <PollDialog onSend={(poll) => store.sendPoll(channelId, poll)} onClose={() => setPolling(false)} />
      )}
      {stamping && <StampDialog onPick={insert} onClose={() => setStamping(false)} />}

      <Popover open={picking} onOpenChange={setPicking}>
        {/*
          Три капли макета v6: «приложить», поле и «отправить». Порознь они
          стоят, пока поле пустое и не в фокусе; стоит в него войти или что-то
          набрать — стекаются в одну форму. Поле больше одной строки — форма
          становится скруглённой плашкой, а не каплей: капля в пять строк
          вышла бы стадионом с полукруглыми торцами.

          Материал — CSS панелей (`composer-goo` в `ui.css`), стекла движка
          в каплях нет: физика только раскладывает их.
        */}
        <GooBar
          className={merged ? "composer-goo merged" : "composer-goo"}
          style={{ height: rowH }}
          height={rowH}
          radius={rowH > ROW ? ROW / 2 : null}
          morph={merged}
          spans={(width) => {
            const gap = merged ? 0 : 10;
            return [
              [0, ROW],
              [ROW + gap, width - ROW - gap],
              [width - ROW, width],
            ];
          }}
        >
          {recording && (
            <button
              type="button"
              className="goo-tool"
              title="Отменить запись (Esc)"
              aria-label="Отменить запись"
              onClick={cancelVoice}
            >
              <IconTrash size={17} />
            </button>
          )}
          {/* «+» есть у всех: «Вставить время» не требует никаких прав. */}
          {!recording && (
            <button
              type="button"
              className="goo-tool"
              title={canPoll ? "Приложить или создать" : "Приложить или вставить время"}
              aria-label={canPoll ? "Приложить или создать" : "Приложить или вставить время"}
              onClick={(event) => {
                const box = event.currentTarget.getBoundingClientRect();
                setPlusAt({ x: box.left, y: box.top });
              }}
              disabled={uploading}
            >
              {uploading ? <Spinner /> : <IconPlus size={18} />}
            </button>
          )}

          {/*
            Панель знаков прижата к правому краю поля и к его верхней кромке —
            та же панель, что открывается из ряда реакций. Поэтому опора —
            поле, а не кнопка знака.
          */}
          <PopoverAnchor asChild>
            <div className={recording ? "composer recording" : "composer"}>
              <span className="composer-sunk" aria-hidden />
              <span className={`composer-ring ${focused ? "on" : ""}`} aria-hidden />
              {recording && voice.current && <VoiceStrip recording={voice.current} />}
                {/*
                  Подсказка пустого поля — тоже в зеркале, а не своей у поля:
                  у `textarea` браузер не обрезает её многоточием, и в узкой
                  колонке чата звонка она просто обрывалась на полуслове.
                */}
                {(pills.length > 0 || !text) && (
                  <div ref={mirror} className="composer-mirror" aria-hidden>
                    {text ? mirrored(text, pills) : <span className="composer-placeholder">{placeholder}</span>}
                  </div>
                )}
                <textarea
                  ref={area}
                  rows={1}
                  value={text}
                  aria-placeholder={placeholder}
                  className={pills.length > 0 ? "has-pills" : undefined}
                  onScroll={(event) => {
                    if (mirror.current) mirror.current.scrollTop = event.currentTarget.scrollTop;
                  }}
                  onChange={(event) => {
                    const moved = rebase(pills, text, event.target.value);
                    pillsNow.current = moved;
                    setPills(moved);
                    setText(event.target.value);

                    /*
                     * Ввод отменяет снятие: набранная буква — это новая просьба,
                     * и дальше решает одно только положение каретки. Снимаем до
                     * `look`, а не после: она читает признак прямо сейчас.
                     */
                    dismissed.current = false;
                    look(event.target.value, event.target.selectionStart);
                    grow();

                    // Пустое поле — не набор: стирание последней буквы не должно
                    // зажигать надпись у остальных.
                    if (event.target.value.trim()) announceTyping();
                  }}
                  onKeyUp={(event) => look(event.currentTarget.value, event.currentTarget.selectionStart)}
                  onPaste={(event) => {
                    /*
                     * Ctrl+V с файлом — то же вложение. Снимок экрана приходит
                     * сюда файлом без имени; имя ему даёт сервер, а не мы.
                     *
                     * Обычную вставку не трогаем: при ней `files` пуст, и текст
                     * ложится как всегда.
                     */
                    const carried = event.clipboardData?.files;
                    if (canUpload && carried?.length) {
                      event.preventDefault();
                      void pick(carried);
                      return;
                    }

                    // Вставили разметку (упоминание, канал, метку) — в поле она встаёт плашками.
                    const pasted = event.clipboardData?.getData("text/plain") ?? "";
                    if (hydrate(pasted, names).pills.length > 0) {
                      event.preventDefault();
                      insert(pasted);
                    }
                  }}
                  onFocus={() => setFocused(true)}
                  onBlur={() => {
                    setHint(null);
                    setFocused(false);
                  }}
                  onKeyDown={(event) => {
                    /*
                     * Ctrl+G — гифки, Ctrl+S — стикеры, Ctrl+E — знаки,
                     * Ctrl+Shift+U — файл, как в Discord. Здесь, а не на окне:
                     * полей бывает два (лента и ветка), и открыться должно то, в
                     * котором пишут.
                     */
                    if ((event.ctrlKey || event.metaKey) && !event.altKey) {
                      const tab: ExpressionTab | null = event.shiftKey
                        ? null
                        : event.code === "KeyG"
                          ? "gif"
                          : event.code === "KeyS"
                            ? "stickers"
                            : event.code === "KeyE"
                              ? "emoji"
                              : null;
                      if (tab) {
                        event.preventDefault();
                        togglePick(tab);
                        return;
                      }
                      if (event.shiftKey && event.code === "KeyU" && canUpload) {
                        event.preventDefault();
                        picker.current?.click();
                        return;
                      }
                    }

                    // Пока открыта подсказка, стрелки и ввод принадлежат ей: отправлять
                    // сообщение выбором имени было бы неожиданно.
                    if (suggestions.length > 0) {
                      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                        event.preventDefault();
                        const step = event.key === "ArrowDown" ? 1 : -1;
                        setChosen((value) => (value + step + suggestions.length) % suggestions.length);
                        return;
                      }

                      if (event.key === "Enter" || event.key === "Tab") {
                        event.preventDefault();
                        put(suggestions[chosen]);
                        return;
                      }

                      if (event.key === "Escape") {
                        event.preventDefault();
                        dismissed.current = true;
                        setHint(null);
                        setAskId(null);
                        return;
                      }
                    }

                    // Esc снимает ответ — но только когда подсказка закрыта: там он
                    // уже занят, и отменять им сразу два действия неожиданно.
                    if (event.key === "Escape" && replyTo) {
                      event.preventDefault();
                      store.setReplyTo(channelId, null);
                      return;
                    }

                    /*
                     * Esc, когда отменять нечего, — как в Discord: канал
                     * прочитан, лента — к последним. Shift+Esc — весь сервер.
                     */
                    // В ветке Esc закрывает ветку, как и прежнее её поле.
                    if (event.key === "Escape" && thread) {
                      event.preventDefault();
                      store.closePanel();
                      return;
                    }

                    if (event.key === "Escape") {
                      event.preventDefault();
                      store.markRead(channelId);
                      void store.jumpToLatest(channelId);
                      if (event.shiftKey && serverId) store.markServerRead(serverId);
                      return;
                    }

                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      void submit();
                      return;
                    }

                    /*
                     * Плашка стирается целиком, как в Discord: Backspace за ней или
                     * Delete перед ней. По букве она развалилась бы в обрывок имени.
                     */
                    const element = event.currentTarget;
                    if (
                      (event.key === "Backspace" || event.key === "Delete") &&
                      element.selectionStart === element.selectionEnd
                    ) {
                      const caret = element.selectionStart;
                      const pill =
                        event.key === "Backspace"
                          ? pillAt(pills, caret)
                          : pills.find((item) => item.start <= caret && caret < item.end);
                      if (pill) {
                        event.preventDefault();
                        const next = text.slice(0, pill.start) + text.slice(pill.end);
                        setPills(rebase(pills, text, next));
                        setText(next);
                        requestAnimationFrame(() => {
                          element.setSelectionRange(pill.start, pill.start);
                          grow();
                        });
                        return;
                      }
                    }

                    // Пустое поле, и ничего не приложено: стрелке здесь двигать нечего.
                    if (
                      event.key === "ArrowUp" &&
                      !event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey &&
                      !text && files.length === 0 && onEditLast
                    ) {
                      event.preventDefault();
                      onEditLast();
                    }
                  }}
                />

              {/*
                Три кнопки, как в Discord: гифки, стикеры, эмодзи — одна панель,
                каждая открывает её на своей вкладке.
              */}
              {(
                [
                  ["gif", "Гифки (Ctrl+G)", <IconImagePlay key="gif" size={16} />],
                  ["stickers", "Стикеры (Ctrl+S)", <IconSticker key="stickers" size={16} />],
                  ["emoji", "Эмодзи (Ctrl+E)", <IconEmoji key="emoji" size={16} />],
                ] as const
              ).map(([tab, title, icon]) => (
                <button
                  key={tab}
                  type="button"
                  data-pick={tab}
                  className={`composer-tool ${picking && pickTab === tab ? "on" : ""}`}
                  title={title}
                  aria-label={title}
                  aria-pressed={picking && pickTab === tab}
                  onClick={() => togglePick(tab)}
                >
                  {icon}
                </button>
              ))}
            </div>
          </PopoverAnchor>

          {/*
            «Отправить» — капля справа. У пустого поля на её месте микрофон —
            голосовое сообщение, как в макете; во время записи она отправляет
            запись.
          */}
          {recording ? (
            <button
              type="button"
              className="composer-send on"
              title="Отправить голосовое (Enter)"
              aria-label="Отправить голосовое"
              onClick={() => void sendVoice()}
            >
              <span className="composer-send-fill" aria-hidden />
              <IconSend size={17} />
            </button>
          ) : !ready && (canVoice || sendingVoice) ? (
            <button
              type="button"
              className="composer-send composer-mic"
              title="Записать голосовое"
              aria-label="Записать голосовое"
              disabled={sendingVoice}
              onClick={() => void startVoice()}
            >
              {sendingVoice ? <Spinner /> : <IconMic size={17} />}
            </button>
          ) : (
            <button
              type="button"
              className={`composer-send ${ready ? "on" : ""}`}
              title="Отправить"
              aria-label="Отправить"
              disabled={!ready}
              onClick={() => void submit()}
            >
              <span className="composer-send-fill" aria-hidden />
              <IconSend size={17} />
            </button>
          )}
        </GooBar>

        <PopoverContent
          side="top"
          align="end"
          sideOffset={0}
          aria-label="Гифки, стикеры и эмодзи"
          /*
           * Фокус ставим сами — на поиск, а не на саму панель: набирают здесь
           * чаще, чем листают, и лишний Tab перед набором стоил бы его каждому
           * открытию.
           */
          onOpenAutoFocus={(event) => event.preventDefault()}
          // Кнопки поля переключают вкладку сами: для панели они не «мимо».
          onInteractOutside={(event) => {
            if ((event.target as HTMLElement | null)?.closest?.("[data-pick]")) event.preventDefault();
          }}
        >
          <ExpressionPicker
            tab={pickTab}
            onTab={setPickTab}
            serverId={serverId}
            custom={custom}
            stickers={stickers}
            server={stickerServer}
            onEmoji={insert}
            onSticker={(id) => void sendSticker(id)}
          />
        </PopoverContent>
      </Popover>

      {/*
        Надпись лежит в нижнем поле обёртки и не двигает разметку: появляйся
        она обычной строкой, лента дёргалась бы вверх-вниз каждый раз, когда
        собеседник начинает и перестаёт печатать.
      */}
      {typing.length > 0 && (
        <div className="composer-typing ellipsis">{typingLine(typing, people)}</div>
      )}

      {suggestions.length > 0 && (
        <div className="mention-hint" role="listbox">
          {hint?.kind === "emoji" && (
            <div className="mention-hint-head">Эмодзи по «:{hint.query}»</div>
          )}
          {!hint && asked && <div className="mention-hint-head">{asked.option.name}: варианты от бота</div>}

          {suggestions.map((item, index) => (
            <Fragment key={suggestionKey(item, index)}>
            {commandHead(suggestions, index) && (
              <div className="mention-hint-head">{commandHead(suggestions, index)}</div>
            )}
            <button
              type="button"
              role="option"
              aria-selected={index === chosen}
              className={`mention-hint-row ${index === chosen ? "on" : ""}`}
              // Мышь роняет фокус раньше нажатия, а с ним и подсказку.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => put(item)}
            >
              {item.kind === "choice" ? (
                <span className="grow ellipsis">{item.choice.name}</span>
              ) : item.kind === "app" ? (
                <>
                  <span className="mention-hint-mark">/</span>
                  <span className="grow ellipsis">
                    /{item.command.name}{" "}
                    <span className="tiny" style={{ color: "var(--ui-text-dim)" }}>
                      {item.command.options
                        .filter((option) => option.type !== OPTION.sub && option.type !== OPTION.group)
                        .map((option) => (option.required ? option.name : `[${option.name}]`))
                        .join(" ")}
                    </span>
                  </span>
                  <span className="tiny ellipsis" style={{ color: "var(--ui-text-dim)" }}>
                    {item.command.description}
                  </span>
                </>
              ) : item.kind === "command" ? (
                <>
                  <span className="mention-hint-mark">/</span>
                  <span className="grow ellipsis">
                    /{item.command.name}{" "}
                    <span className="tiny" style={{ color: "var(--ui-text-dim)" }}>
                      {item.command.args}
                    </span>
                  </span>
                  <span className="tiny" style={{ color: "var(--ui-text-dim)" }}>
                    {item.command.hint}
                  </span>
                </>
              ) : item.kind === "person" ? (
                <>
                  <span className="grow ellipsis">
                    {item.person.display_name || item.person.username}
                  </span>
                  <span className="mono tiny" style={{ color: "var(--ui-text-dim)" }}>
                    {item.person.handle}
                  </span>
                </>
              ) : item.kind === "everyone" ? (
                <>
                  <span className="mention-hint-mark">@</span>
                  <span className="grow ellipsis">@{item.word}</span>
                  <span className="tiny" style={{ color: "var(--ui-text-dim)" }}>
                    {item.word === "everyone" ? "все, кто видит канал" : "кто из них в сети"}
                  </span>
                </>
              ) : item.kind === "role" ? (
                <>
                  <span className="role-rank" />
                  <span className="grow ellipsis">{item.role.name}</span>
                  <span className="tiny" style={{ color: "var(--ui-text-dim)" }}>
                    роль
                  </span>
                </>
              ) : item.kind === "channel" ? (
                <>
                  <span className="mention-hint-mark">
                    {item.channel.channel_type === "stage" ? (
                      <IconStageChannel size={13} />
                    ) : isCall(item.channel.channel_type) ? (
                      <IconVoiceChannel size={13} />
                    ) : (
                      <IconTextChannel size={13} />
                    )}
                  </span>
                  <span className="grow ellipsis">{item.channel.name}</span>
                </>
              ) : (
                <>
                  <span className="mention-hint-mark">
                    {item.custom ? (
                      <FileImage
                        id={item.custom.file_id}
                        alt={item.token}
                        className="emoji-image"
                      />
                    ) : (
                      item.mark
                    )}
                  </span>
                  <span className="mono grow ellipsis">{item.token}</span>
                  {item.custom && (
                    <span className="tiny" style={{ color: "var(--ui-accent-text)" }}>
                      {item.custom.server_name}
                    </span>
                  )}
                </>
              )}
            </button>
            </Fragment>
          ))}

          {hint?.kind === "emoji" && (
            <div className="mention-hint-foot">
              <IconEnter size={11} className="align-[-0.15em]" /> вставить ·{" "}
              <IconTab size={11} className="align-[-0.15em]" /> следующий
            </div>
          )}
        </div>
      )}

      {suggestions.length === 0 && typedApp && <CommandCard typed={typedApp} onPick={addOption} />}

      <div className="composer-foot">
        {text.length > 1800 && <span>{2048 - text.length} знаков осталось</span>}
      </div>
    </div>
  );
}

/**
 * Карточка набираемой команды бота — над полем, как подсказка параметров в
 * Discord: чья команда, что делает, какие параметры и какой из них набирают.
 * Щелчок по параметру дописывает его имя.
 */
function CommandCard({ typed, onPick }: { typed: Typed; onPick: (option: CommandOption) => void }) {
  const now = currentOption(typed);
  const subs = typed.options.some((option) => option.type === OPTION.sub || option.type === OPTION.group);
  return (
    <div className="mention-hint command-card" role="listbox">
      <div className="mention-hint-head">
        {typed.command.bot.display_name || typed.command.bot.username} · /{[typed.command.name, ...typed.path].join(" ")}
      </div>
      <div className="command-card-about tiny">{typed.command.description}</div>
      {typed.options.map((option) => (
        <button
          key={option.name}
          type="button"
          role="option"
          aria-selected={option === now}
          className={`mention-hint-row ${option === now ? "on" : ""}`}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onPick(option)}
        >
          <span className="mention-hint-mark">{subs ? "/" : option.required ? "•" : "◦"}</span>
          <span className="grow ellipsis">
            {option.name}
            {!subs && (
              <span className="tiny" style={{ color: "var(--ui-text-dim)" }}>
                {" "}
                {optionKind(option)}
                {option.choices?.length ? `: ${option.choices.map((choice) => choice.name).join(", ")}` : ""}
              </span>
            )}
          </span>
          <span className="tiny ellipsis" style={{ color: "var(--ui-text-dim)" }}>
            {option.description}
            {!subs && !option.required ? " · необязательный" : ""}
          </span>
        </button>
      ))}
      {typed.options.length === 0 && <div className="command-card-about tiny">Без параметров — Enter, чтобы вызвать</div>}
    </div>
  );
}

/** Идёт запись: красная точка, время и живая волна последних отсчётов. */
function VoiceStrip({ recording }: { recording: VoiceRecording }) {
  const levels = recording.levels().slice(-48);
  const seconds = Math.floor(recording.elapsed() / 1000);
  return (
    <div className="voice-rec" role="status" aria-label="Идёт запись голосового">
      <span className="voice-rec-dot" aria-hidden />
      <span className="voice-rec-time">
        {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}
      </span>
      <span className="voice-rec-wave" aria-hidden>
        {levels.map((level, index) => (
          <span key={index} style={{ height: `${Math.max(8, Math.round(level * 100))}%` }} />
        ))}
      </span>
      <span className="voice-rec-hint">Enter — отправить, Esc — отменить</span>
    </div>
  );
}

/** Описание вложения — как «Добавить описание» в Discord: для читалки экрана. */
function DescribeDialog({
  file,
  onSave,
  onClose,
}: {
  file: QueuedFile;
  onSave: (description: string | undefined) => void;
  onClose: () => void;
}) {
  const [text, setText] = useState(file.description ?? "");
  return (
    <Dialog
      title="Описание вложения"
      onClose={onClose}
      actions={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button type="button" onClick={() => onSave(text.trim() || undefined)}>
            Сохранить
          </Button>
        </>
      }
    >
      <Field label={file.name} hint="Его прочитает читалка экрана, а остальные увидят по наведению.">
        <Textarea
          value={text}
          maxLength={1024}
          rows={3}
          autoFocus
          placeholder="Что на картинке"
          onChange={(event) => setText(event.target.value)}
        />
      </Field>
    </Dialog>
  );
}
